import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import type { WorldCupBookmakerMarkets, WorldCupTeam } from './use-world-cup-gamelines';

export interface WorldCupExactScoreData {
  slug: string;
  title?: string;
  home_team?: WorldCupTeam;
  away_team?: WorldCupTeam;
  game_start_time?: string;
  game_event_id?: string | null;
  exact_score: WorldCupBookmakerMarkets | null;
}

export interface WorldCupExactScoreResponse {
  success: boolean;
  data: WorldCupExactScoreData;
  message?: string;
}

export interface UseWorldCupExactScoreOptions {
  eventSlug?: string | null;
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupExactScoreResponse, Error>;
}

export async function fetchWorldCupExactScore(eventSlug: string) {
  const searchParams = new URLSearchParams({
    event_slug: eventSlug,
  });

  const response = await customFetch(`/api/world-cup/game/exact-score?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup exact score');
  }

  const result = (await response.json()) as WorldCupExactScoreResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup exact score');
  }

  return result;
}

export function useWorldCupExactScore(options: UseWorldCupExactScoreOptions = {}) {
  const eventSlug = options.eventSlug?.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['world-cup-exact-score', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    WorldCupExactScoreResponse,
    Error
  >(swrKey, () => fetchWorldCupExactScore(eventSlug!), {
    revalidateOnFocus: false,
    ...options.swr,
  });

  return {
    data: data ?? null,
    exactScore: data?.data?.exact_score ?? null,
    detail: data?.data ?? null,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupExactScore;
